import React from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { cn } from "@/utils/cn";
import { UnifiedAuthenticatedImage } from "./UnifiedAuthenticatedImage";

interface VideoThumbnailWithFallbackProps {
  mediaItem: MediaItem;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  showPlayButton?: boolean;
}

/**
 * Legacy wrapper around UnifiedAuthenticatedImage for video thumbnails
 * @deprecated Use UnifiedAuthenticatedImage directly with useThumbnail=true for new components
 */
export const VideoThumbnailWithFallback: React.FC<VideoThumbnailWithFallbackProps> = ({
  mediaItem,
  className,
  onClick,
  showPlayButton = true,
}) => {
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    }
    return `0:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg bg-gray-100 cursor-pointer transition-all duration-200",
        "aspect-9/16", // Videos should always be 9:16 (vertical/portrait)
        className,
      )}
      onClick={onClick}
    >
      <UnifiedAuthenticatedImage
        mediaItem={mediaItem}
        aspectRatio="9:16"
        useThumbnail={true}
        className="w-full h-full"
        alt={`Video: ${mediaItem.originalFilename}`}
        loading="lazy"
      />

      {/* Play Button Overlay */}
      {showPlayButton && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity duration-200 z-20">
          <div className="bg-white/90 rounded-full p-3 shadow-lg">
            <svg
              className="w-6 h-6 text-gray-800 ml-0.5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      )}

      {/* Video Duration Badge */}
      {mediaItem.frontendData.duration && (
        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-sm z-20">
          {formatDuration(mediaItem.frontendData.duration)}
        </div>
      )}

      {/* Processing Status Badge */}
      {mediaItem.processingStatus === "processing" && (
        <div className="absolute top-2 right-2 bg-amber-500 text-white text-xs px-2 py-1 rounded-sm font-medium z-20 flex items-center gap-1">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
          Processing
        </div>
      )}
    </div>
  );
};